import { type AccountModuleKey } from '@app/reducers/account-module/account-module.model';
import { LockType } from '@app/services/plan-lock/plan-lock-dialog.model';
import { _ } from '@ngx-translate/core';
import { PlanType } from '@shiftbase-com/ts-shared/models';

import { type PayrollStepDefinition, type PayrollStepId } from './payroll.model';
import { MINIJOB_STEP_PERMISSION } from './steps/minijob/payroll-minijob';

/**
 * Every step the flow knows about, in the fixed order a run works through them. Every account on
 * the flow gets every one of them that its modules allow (`payrollStepsForModules`);
 * `planLockedSteps` in payroll-progress.ts says which are behind a plan lock rather than which
 * are absent.
 *
 * The four permissions still tagged `// SHB-24214` are provisional — the closest existing
 * permission for the step's action, pending the per-step inventory that ticket owes. The lock
 * step's is not: it is the same check the timesheet page runs before offering the very dialog
 * that step opens.
 */
export const payrollStepDefinitions: PayrollStepDefinition[] = [
  {
    id: 'absences',
    labelKey: _('Absence approval'),
    descriptionKey: _('Approve or decline pending absences'),
    icon: 'calendar-cute-re',
    permission: 'View absentee', // SHB-24214
    canSkip: true,
  },
  {
    id: 'timesheets',
    labelKey: _('Timesheet approval'),
    descriptionKey: _('Approve or decline all timesheets'),
    icon: 'stopwatch-cute-re',
    permission: 'Approve timesheets', // SHB-24214
    canSkip: true,
  },
  {
    id: 'minijob',
    labelKey: _('Minijob payout'),
    descriptionKey: _('Prepare monthly Minijob payouts'),
    icon: 'pig-money-cute-re',
    // Read-only mode for 'View minijob' waits on the backend admitting that permission.
    permission: MINIJOB_STEP_PERMISSION,
    module: 'minijob',
    canSkip: true,
  },
  {
    id: 'balances',
    labelKey: _('Correct plus/min hours'),
    descriptionKey: _('Review and correct plus/min balances'),
    icon: 'balance-cute-re',
    permission: 'Edit plus min hours', // SHB-24214
    canSkip: true,
  },
  {
    id: 'lock',
    labelKey: _('Lock timesheets'),
    descriptionKey: _('Close days to lock the period'),
    icon: 'lock-cute-re',
    // The pair the timesheet's own Open/Close days entry point checks, any-of: either half is
    // enough to work the dialog this step opens.
    permission: ['Close timesheets', 'Open timesheets'],
    // Close Day is premium-only and stays that way, so a basic account sees this step locked. The
    // dialog is the one the timesheet's own Open/Close days entry point opens, so the flow makes
    // the identical promise about the identical feature.
    planLock: { plan: PlanType.PREMIUM, lockType: LockType.OPEN_CLOSE_DAYS },
    canSkip: true,
  },
  {
    id: 'report',
    // The report itself, so it cannot be skipped and declares no backlog to clear.
    labelKey: _('Preview payroll data'),
    descriptionKey: _('Check what you will export or send'),
    icon: 'chart-vertical-cute-re',
    permission: 'View reports', // SHB-24214
    canSkip: false,
  },
];

/** The definition of one step. Every step id has one, whatever the account's modules or plan. */
export function payrollStepDefinition(id: PayrollStepId): PayrollStepDefinition {
  const definition = payrollStepDefinitions.find((step) => step.id === id);

  if (!definition) {
    throw new Error(`No payroll step definition for '${id}'`);
  }

  return definition;
}

/**
 * The definitions an account actually runs: a module-gated step is left out — not locked, not
 * blocked — when its module is off.
 */
export function payrollStepsForModules(
  definitions: PayrollStepDefinition[],
  enabledModules: AccountModuleKey[],
): PayrollStepDefinition[] {
  return definitions.filter((step) => !step.module || enabledModules.includes(step.module));
}
